/**
 * Interactive Demo Section
 * Main demo interface with prompt input and decision display
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { PromptInput } from '@/components/demo/prompt-input';
import { DecisionPanel } from '@/components/demo/decision-panel';
import type { RoutingDecision, PromptFeatures } from '@/lib/types';
import { analyzePrompt, extractPromptFeatures } from '@/lib/demo/routing-engine';

export function DemoSection() {
  const [prompt, setPrompt] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [decision, setDecision] = useState<RoutingDecision | null>(null);
  const [features, setFeatures] = useState<PromptFeatures | null>(null);

  const handleAnalyze = async () => {
    if (!prompt.trim()) return;

    setIsAnalyzing(true);
    setDecision(null);
    setFeatures(null);

    try {
      // Extract features
      const extractedFeatures = extractPromptFeatures(prompt);
      setFeatures(extractedFeatures);

      // Simulate routing decision with delay
      const result = await analyzePrompt(prompt);
      setDecision(result);
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleReset = () => {
    setPrompt('');
    setDecision(null);
    setFeatures(null);
  };

  return (
    <section className="py-16 px-4" id="demo">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="backdrop-blur-md bg-background/60 border border-border/50 rounded-2xl p-8 md:p-12 shadow-xl">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              See Pathfinder Route in Real-Time
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Try our demo to understand how intelligent routing works.
              <br />
              No sign-up required. Your prompts stay in your browser.
            </p>
          </div>
        </motion.div>

        {/* Demo Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          <PromptInput
            value={prompt}
            onChange={setPrompt}
            onAnalyze={handleAnalyze}
            isAnalyzing={isAnalyzing}
          />

          <DecisionPanel
            decision={decision}
            features={features}
            isLoading={isAnalyzing}
            originalPrompt={prompt}
            onReset={handleReset}
          />
        </div>

        {/* Cost Comparison Table */}
        {decision && (
          <motion.div
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="backdrop-blur-md bg-background/50 border border-border/50 rounded-xl p-6 shadow-xl">
              <h3 className="text-xl font-semibold mb-6 text-center">
                Comparison: How Different Policies Would Route
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-semibold">Policy</th>
                      <th className="text-left py-3 px-4 font-semibold">Model</th>
                      <th className="text-left py-3 px-4 font-semibold">Cost</th>
                      <th className="text-left py-3 px-4 font-semibold">Savings</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border bg-primary/5">
                      <td className="py-3 px-4 font-semibold">Pathfinder</td>
                      <td className="py-3 px-4">{decision.selectedModel.name}</td>
                      <td className="py-3 px-4">
                        ${decision.estimatedCost.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">Baseline</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4">OpenRouter</td>
                      <td className="py-3 px-4">{decision.selectedModel.name}</td>
                      <td className="py-3 px-4">
                        ${(decision.estimatedCost * 1.15).toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-red-500">↑ 15% more</td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-3 px-4">LiteLLM</td>
                      <td className="py-3 px-4">GPT-5</td>
                      <td className="py-3 px-4">$2.75</td>
                      <td className="py-3 px-4 text-red-500">↑ 8% more</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Always Premium</td>
                      <td className="py-3 px-4">Claude Opus 4.1</td>
                      <td className="py-3 px-4">$5.00</td>
                      <td className="py-3 px-4 text-green-500">
                        ↓ {decision.savings.vsAlwaysPremium}% with Pathfinder
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
